import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import {
  letterVariants,
  heroSubVariants,
  heroBtnsVariants,
  revealVariants,
  projectRowVariants,
  scrollIndicatorVariants,
  navLinkVariants,
} from '@/lib/animations';
import { useScrollAnimation } from '@/lib/useScrollAnimation';
import SpaceHero from '@/components/SpaceHero';
import '../styles/portfolio.css';

import restaurantsImg from '../img/restaurants.png';
import lowiinImg from '../img/lowiinproject.png';
import lemmingsImg from '../img/lemmings.png';
import gravitysysImg from '../img/gravitysys.png';
import spymasterImg from '../img/spymaster.png';
import snakegameImg from '../img/snakegame.png';
import keysImg from '../img/keys.png';
import rustserverImg from '../img/rustserver.png';

gsap.registerPlugin(ScrollTrigger);

interface Blob {
  x: number;
  y: number;
  r: number;
  c: string;
  vx: number;
  vy: number;
}

interface SkillItem {
  num: string;
  name: string;
  variant: 'v1' | 'v2' | 'v3';
  exp: string;
  level: number;
}

interface ProjectItem {
  num: string;
  name: string;
  tag: string;
  img: string;
}

const SKILLS: SkillItem[] = [
  { num: '01', name: 'JavaScript', variant: 'v1', exp: '7+ ans · Front & Back', level: 0.9 },
  { num: '02', name: 'React', variant: 'v2', exp: 'Framework principal', level: 0.85 },
  { num: '03', name: 'Python', variant: 'v3', exp: 'ML · Simulation · Scripts', level: 0.88 },
  { num: '04', name: 'PHP', variant: 'v1', exp: 'Backend · API', level: 0.75 },
  { num: '05', name: 'C#', variant: 'v2', exp: 'Applications · Jeux', level: 0.7 },
  { num: '06', name: 'Vue.js', variant: 'v3', exp: 'Interfaces réactives', level: 0.78 },
];

const PROJECTS: ProjectItem[] = [
  { num: '—01', name: 'DIFTIP', tag: 'Web App · React', img: restaurantsImg },
  { num: '—02', name: 'LOWIIN', tag: 'Website', img: lowiinImg },
  { num: '—03', name: 'IA SHAPE', tag: 'Machine Learning · Python', img: lemmingsImg },
  { num: '—04', name: 'GRAVITY SYS', tag: 'Simulation · PyGame', img: gravitysysImg },
  { num: '—05', name: 'THE SPYMASTER', tag: 'Roman · Fantasy', img: spymasterImg },
  { num: '—06', name: 'SNAKE GAME', tag: 'Jeu · Python', img: snakegameImg },
  { num: '—07', name: 'ENCRYPTION', tag: 'Cryptographie · Python', img: keysImg },
  { num: '—08', name: 'RUST SERVER', tag: 'Server Admin', img: rustserverImg },
];

const CHARS = '!<>_\\/[]{}=+*^?#ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

function scramble(el: HTMLElement, finalTxt: string, ms = 1100): void {
  let it = 0;
  const tot = Math.ceil(ms / 30);
  const orig = finalTxt.split('');
  const iv = setInterval(() => {
    el.textContent = orig
      .map((c, i) => {
        if (c === ' ') return ' ';
        if (it / tot > i / orig.length) return c;
        return CHARS[Math.floor(Math.random() * CHARS.length)];
      })
      .join('');
    it++;
    if (it >= tot) {
      el.textContent = finalTxt;
      clearInterval(iv);
    }
  }, 30);
}

interface LetterProps {
  char: string;
  index: number;
  allRefs: React.MutableRefObject<(HTMLSpanElement | null)[]>;
  globalOffset: number;
}

const Letter: React.FC<LetterProps> = ({ char, index, allRefs, globalOffset }) => {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    allRefs.current[globalOffset + index] = ref.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnter = () => {
    const all = allRefs.current;
    const g = globalOffset + index;
    if (all[g]) gsap.to(all[g]!, { y: -14, duration: 0.22, ease: 'power2.out' });
    if (all[g - 1]) gsap.to(all[g - 1]!, { y: -6, duration: 0.18, ease: 'power2.out' });
    if (all[g + 1]) gsap.to(all[g + 1]!, { y: -6, duration: 0.18, ease: 'power2.out' });
  };

  const handleLeave = () => {
    const all = allRefs.current;
    const g = globalOffset + index;
    if (all[g]) gsap.to(all[g]!, { y: 0, duration: 0.5, ease: 'elastic.out(1,.5)' });
    if (all[g - 1]) gsap.to(all[g - 1]!, { y: 0, duration: 0.45, ease: 'elastic.out(1,.5)' });
    if (all[g + 1]) gsap.to(all[g + 1]!, { y: 0, duration: 0.45, ease: 'elastic.out(1,.5)' });
  };

  return (
    <motion.span
      ref={ref}
      className="letter"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      variants={letterVariants}
      custom={index}
      initial="hidden"
      animate="visible"
    >
      {char}
    </motion.span>
  );
};

const SkillCard: React.FC<{ skill: SkillItem; index: number }> = ({ skill, index }) => {
  return (
    <motion.div
      className="sk-panel card"
      variants={{
        hidden: { opacity: 0, x: -30 },
        visible: {
          opacity: 1,
          x: 0,
          transition: { delay: index * 0.1, duration: 0.6, ease: 'easeOut' },
        },
      }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ x: 8 }}
    >
      <span className="sk-num">{skill.num}</span>
      <div className={`sk-name ${skill.variant}`}>{skill.name}</div>
      <div className="sk-exp">{skill.exp}</div>
      <div className="sk-bar">
        <motion.div
          className="sk-bar-fill"
          data-w={skill.level}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: skill.level }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{ delay: index * 0.1 + 0.2, duration: 1.1, ease: 'easeOut' }}
          style={{ transformOrigin: 'left' }}
        />
      </div>
    </motion.div>
  );
};

const ProjectRow: React.FC<{ project: ProjectItem; index: number }> = ({ project, index }) => {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.4 });
  const [isHovering, setIsHovering] = useState(false);

  return (
    <motion.div
      ref={ref}
      className="proj-row"
      data-img={project.img}
      variants={projectRowVariants}
      custom={index}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      whileHover="whileHover"
      onHoverStart={() => setIsHovering(true)}
      onHoverEnd={() => setIsHovering(false)}
    >
      <div className="proj-left">
        <span className="proj-num">{project.num}</span>
        <motion.span
          className="proj-name"
          animate={isHovering ? { color: 'rgba(92, 225, 230, 1)' } : { color: 'rgba(240, 248, 255, 1)' }}
          transition={{ duration: 0.3 }}
        >
          {project.name}
        </motion.span>
      </div>
      <div className="proj-right">
        <span className="proj-tag">{project.tag}</span>
        <motion.span
          className="proj-arrow"
          animate={isHovering ? { x: 6 } : { x: 0 }}
          transition={{ duration: 0.3 }}
        >
          →
        </motion.span>
      </div>
    </motion.div>
  );
};

const Portfolio: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const mobRef = useRef<HTMLDivElement>(null);
  const curRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const curImgRef = useRef<HTMLDivElement>(null);
  const curImgElRef = useRef<HTMLImageElement>(null);
  const eyebrowRef = useRef<HTMLParagraphElement>(null);
  const heroBtnsRef = useRef<HTMLDivElement>(null);
  const skillsTrackRef = useRef<HTMLDivElement>(null);
  const allLetterRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const [mobOpen, setMobOpen] = useState(false);
  const { ref: projHeaderRef, isInView: projHeaderInView } = useScrollAnimation({ threshold: 0.5 });

  /* Aurora canvas animé en fond */
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const cx = cv.getContext('2d')!;
    let W = 0,
      H = 0,
      rafId = 0;

    const resize = () => {
      W = cv.width = window.innerWidth;
      H = cv.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const blobs: Blob[] = [
      { x: 0.12, y: 0.3, r: 0.5, c: '#1a3a7a', vx: 0.00013, vy: 0.00009 },
      { x: 0.8, y: 0.6, r: 0.44, c: '#33bbcf', vx: -0.0001, vy: 0.00014 },
      { x: 0.5, y: 0.85, r: 0.38, c: '#082050', vx: 0.00007, vy: -0.0001 },
      { x: 0.88, y: 0.18, r: 0.3, c: '#5ce1e6', vx: -0.0001, vy: 0.00008 },
    ];

    const draw = () => {
      cx.clearRect(0, 0, W, H);
      cx.fillStyle = '#00040f';
      cx.fillRect(0, 0, W, H);
      blobs.forEach((b) => {
        b.x += b.vx;
        b.y += b.vy;
        if (b.x < 0 || b.x > 1) b.vx *= -1;
        if (b.y < 0 || b.y > 1) b.vy *= -1;
        const g = cx.createRadialGradient(b.x * W, b.y * H, 0, b.x * W, b.y * H, b.r * Math.min(W, H));
        g.addColorStop(0, b.c + '26');
        g.addColorStop(1, 'transparent');
        cx.fillStyle = g;
        cx.fillRect(0, 0, W, H);
      });
      rafId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  /* Lenis smooth scroll + sync GSAP ScrollTrigger */
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t: number) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    lenis.on('scroll', () => {
      navRef.current?.classList.toggle('stuck', lenis.scroll > 50);
    });
    return () => {
      lenis.destroy();
    };
  }, []);

  /* Curseur custom avec lag sur l'anneau */
  useEffect(() => {
    let mx = window.innerWidth / 2,
      my = window.innerHeight / 2;
    let rx = mx,
      ry = my,
      rafId = 0;

    const onMove = (e: MouseEvent) => {
      mx = e.clientX;
      my = e.clientY;
    };
    window.addEventListener('mousemove', onMove);

    const loop = () => {
      rx += (mx - rx) * 0.13;
      ry += (my - ry) * 0.13;
      if (curRef.current) {
        curRef.current.style.left = `${mx}px`;
        curRef.current.style.top = `${my}px`;
      }
      if (ringRef.current) {
        ringRef.current.style.left = `${rx}px`;
        ringRef.current.style.top = `${ry}px`;
      }
      if (curImgRef.current) {
        curImgRef.current.style.left = `${mx + (rx - mx) * 0.4}px`;
        curImgRef.current.style.top = `${my + (ry - my) * 0.4}px`;
      }
      rafId = requestAnimationFrame(loop);
    };
    loop();

    const onEnter = () => {
      curRef.current?.classList.add('big');
      ringRef.current?.classList.add('big');
    };
    const onLeave = () => {
      curRef.current?.classList.remove('big');
      ringRef.current?.classList.remove('big');
    };
    document.querySelectorAll('a, button, .proj-row, .sk-panel.card').forEach((el) => {
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
    });

    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  /* Animations d'entrée hero + scramble */
  useEffect(() => {
    if (eyebrowRef.current) {
      scramble(eyebrowRef.current, 'Future Software Engineer — Geneva, SUI', 900);
    }

    document.querySelectorAll<HTMLAnchorElement>('.nlink').forEach((a) => {
      const orig = a.textContent ?? '';
      a.addEventListener('mouseenter', () => scramble(a, orig, 450));
    });
  }, []);

  /* Boutons magnétiques */
  useEffect(() => {
    const cleanup: (() => void)[] = [];
    document.querySelectorAll<HTMLElement>('.mag-btn').forEach((btn) => {
      const onMove = (e: MouseEvent) => {
        const rc = btn.getBoundingClientRect();
        gsap.to(btn, {
          x: ((e.clientX - rc.left - rc.width / 2) * 0.32),
          y: ((e.clientY - rc.top - rc.height / 2) * 0.32),
          duration: 0.4,
          ease: 'power3.out',
        });
      };
      const onLeave = () =>
        gsap.to(btn, { x: 0, y: 0, duration: 0.55, ease: 'elastic.out(1,.5)' });
      btn.addEventListener('mousemove', onMove as EventListener);
      btn.addEventListener('mouseleave', onLeave);
      cleanup.push(() => {
        btn.removeEventListener('mousemove', onMove as EventListener);
        btn.removeEventListener('mouseleave', onLeave);
      });
    });
    return () => cleanup.forEach((fn) => fn());
  }, []);

  /* Skills — scroll horizontal piné avec GSAP ScrollTrigger */
  useEffect(() => {
    const track = skillsTrackRef.current;
    if (!track) return;
    const panels = track.querySelectorAll('.sk-panel');
    const dist = () => (panels.length - 1) * window.innerWidth;

    const st = ScrollTrigger.create({
      trigger: '.skills-wrap',
      pin: true,
      scrub: 1.2,
      start: 'top top',
      end: () => '+=' + dist(),
      invalidateOnRefresh: true,
      anticipatePin: 1,
      onUpdate: (self: any) => gsap.set(track, { x: -self.progress * dist() }),
    });

    return () => {
      st.kill();
    };
  }, []);

  /* Tesla-style animations: hero parallax + section clip-path reveals */
  useEffect(() => {
    // Hero parallax — contenu monte en scrollant (effet Tesla)
    if (heroContentRef.current) {
      gsap.to(heroContentRef.current, {
        y: -180,
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero',
          start: 'top top',
          end: 'bottom top',
          scrub: 1.2,
        },
      });
    }

    // Clip-path reveal sur les titres de section (signature Tesla)
    const revealEls = document.querySelectorAll<HTMLElement>('.clip-reveal');
    revealEls.forEach((el) => {
      gsap.fromTo(
        el,
        { clipPath: 'inset(100% 0 0 0)', y: 40 },
        {
          clipPath: 'inset(0% 0 0 0)',
          y: 0,
          duration: 1.1,
          ease: 'power4.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    // Fade-up stagger sur les blocs de contenu (sous-titres, descriptions)
    const fadeEls = document.querySelectorAll<HTMLElement>('.fade-up');
    fadeEls.forEach((el, i) => {
      gsap.fromTo(
        el,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          delay: (el.dataset.delay ? parseFloat(el.dataset.delay) : i * 0.12),
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    ScrollTrigger.refresh();
  }, []);

  /* Projets — image qui suit le curseur + scramble du nom */
  useEffect(() => {
    const cleanup: (() => void)[] = [];
    document.querySelectorAll<HTMLElement>('.proj-row').forEach((row) => {
      const src = row.dataset.img ?? '';
      const name = row.querySelector<HTMLElement>('.proj-name');
      if (!name) return;
      const orig = name.textContent ?? '';
      const onEnter = () => {
        if (curImgElRef.current) curImgElRef.current.src = src;
        curImgRef.current?.classList.add('show');
        scramble(name, orig, 550);
      };
      const onLeave = () => {
        curImgRef.current?.classList.remove('show');
        name.textContent = orig;
      };
      row.addEventListener('mouseenter', onEnter);
      row.addEventListener('mouseleave', onLeave);
      cleanup.push(() => {
        row.removeEventListener('mouseenter', onEnter);
        row.removeEventListener('mouseleave', onLeave);
      });
    });
    return () => cleanup.forEach((fn) => fn());
  }, []);

  return (
    <>
      <SpaceHero />

      <div ref={curRef} className="cur" />
      <div ref={ringRef} className="cur-ring" />
      <div ref={curImgRef} className="cur-img">
        <img ref={curImgElRef} src="" alt="" />
      </div>

      {/* Navigation */}
      <motion.nav
        ref={navRef}
        className="pf-nav"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <motion.a
          href="#home"
          className="nav-logo"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          JM<span>.</span>
        </motion.a>
        <ul className="nav-links">
          <li>
            <motion.a href="#skills" className="nlink" variants={navLinkVariants} initial="rest" whileHover="hover">
              Skills
            </motion.a>
          </li>
          <li>
            <motion.a href="#projets" className="nlink" variants={navLinkVariants} initial="rest" whileHover="hover">
              Projets
            </motion.a>
          </li>
          <li>
            <motion.a href="#contact" className="nlink" variants={navLinkVariants} initial="rest" whileHover="hover">
              Contact
            </motion.a>
          </li>
        </ul>
        <motion.button
          className="brgr"
          onClick={() => setMobOpen((v) => !v)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <span />
          <span />
          <span />
        </motion.button>
      </motion.nav>

      <AnimatePresence>
        {mobOpen && (
          <motion.div
            ref={mobRef}
            className="mob-nav open"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.a
              href="#skills"
              onClick={() => setMobOpen(false)}
              whileHover={{ x: 8, color: 'rgba(92, 225, 230, 1)' }}
            >
              Skills
            </motion.a>
            <motion.a
              href="#projets"
              onClick={() => setMobOpen(false)}
              whileHover={{ x: 8, color: 'rgba(92, 225, 230, 1)' }}
            >
              Projets
            </motion.a>
            <motion.a
              href="#contact"
              onClick={() => setMobOpen(false)}
              whileHover={{ x: 8, color: 'rgba(92, 225, 230, 1)' }}
            >
              Contact
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="hero" id="home">
        <div ref={heroContentRef} className="hero-content">
          <motion.p
            ref={eyebrowRef}
            className="eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Future Software Engineer — Geneva, SUI
          </motion.p>

          <h1 className="hero-title">
            <motion.span className="word row1">
              {['J', 'A', 'S', 'O', 'N'].map((char, i) => (
                <Letter key={i} char={char} index={i} allRefs={allLetterRefs} globalOffset={0} />
              ))}
            </motion.span>
            <span className="sep" />
            <motion.span className="word row2">
              {['M', 'O', 'U', 'R', 'I', 'E', 'R'].map((char, i) => (
                <Letter key={i} char={char} index={i} allRefs={allLetterRefs} globalOffset={5} />
              ))}
            </motion.span>
          </h1>

          <motion.p
            className="hero-sub"
            variants={heroSubVariants}
            initial="hidden"
            animate="visible"
          >
            Building websites, apps &amp; software.<br />
            Always thinking about creating something bigger.
          </motion.p>

          <motion.div
            ref={heroBtnsRef}
            className="hero-btns"
            variants={heroBtnsVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.a
              href="#projets"
              className="btn-fill mag-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              See my projects
            </motion.a>
            <motion.a
              href="#contact"
              className="btn-line mag-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Contact
            </motion.a>
          </motion.div>
        </div>

        <motion.div
          className="scroll-ind"
          animate="animate"
          variants={scrollIndicatorVariants}
        >
          <span>Scroll</span>
          <div className="scroll-track" />
        </motion.div>
      </section>

      {/* Skills — scroll horizontal */}
      <section className="skills-wrap" id="skills">
        <div className="skills-sticky">
          <div className="skills-track" ref={skillsTrackRef}>
            <motion.div
              className="sk-panel intro"
              variants={{
                hidden: { opacity: 0, x: -30 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h2 className="clip-reveal">SKILLS &amp;<br /><em>EXPERTISE</em></h2>
              <p className="fade-up" data-delay="0.3">
                Python, JS, PHP, C# — and frameworks like React and Vue — help me build everything I want from scratch, and I'm loving it!"<br /><br />
                → <a href="https://github.com/Jrix-G" target="_blank" rel="noreferrer">GitHub ↗</a>
              </p>
            </motion.div>
            {SKILLS.map((sk, i) => (
              <SkillCard key={sk.num} skill={sk} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Projets */}
      <section className="proj-sec" id="projets">
        <motion.div
          ref={projHeaderRef}
          className="proj-header"
          variants={revealVariants}
          initial="hidden"
          animate={projHeaderInView ? 'visible' : 'hidden'}
        >
          <h2 className="clip-reveal">PORTFOLIO &amp; <em>PROJETS</em></h2>
        </motion.div>
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.08,
                delayChildren: 0.2,
              },
            },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
        >
          {PROJECTS.map((proj, i) => (
            <ProjectRow key={proj.num} project={proj} index={i} />
          ))}
        </motion.div>
      </section>

      {/* Contact */}
      <motion.section
        className="contact-sec"
        id="contact"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="contact-top"
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <h2 className="contact-h clip-reveal">
            LET'S<br /><em>WORK</em><br />TOGETHER
          </h2>
          <div className="contact-right">
            <p className="fade-up" data-delay="0.4">Available to discuss about an idea, or for a freelance project</p>
          </div>
        </motion.div>

        <motion.a
          href="mailto:contact@jasonmourier.dev"
          className="email-big mag-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          contact@me
        </motion.a>

        <motion.div
          className="term"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ delay: 0.4, duration: 0.7 }}
        >
          <div className="term-top">
            <div className="dot r" />
            <div className="dot o" />
            <div className="dot g" />
            <span className="term-label">jason@portfolio ~ contact</span>
          </div>
          <div className="term-body">
            <div className="tl">
              <span className="tp">jason@portfolio:~$ </span>
              <span className="tc">whoami</span>
            </div>
            <div className="tl to">Jason Mourier — Future Software Engineer (I hope so!)</div>
            <br />
            <div className="tl">
              <span className="tp">jason@portfolio:~$ </span>
              <span className="tc">cat contact.txt</span>
            </div>
            <div className="tl to">Open to work · freelance · collabs :)</div>
            <div className="tl to hi">
              → <a href="mailto:jrixweb@gmail.com">jrixweb@gmail.com</a>
            </div>
            <br />
            <div className="tl">
              <span className="tp">jason@portfolio:~$ </span>
              <span className="blink" />
            </div>
          </div>
        </motion.div>

        <motion.div
          className="contact-btns"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.15,
              },
            },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.a
            href="mailto:contact@jasonmourier.dev"
            className="btn-fill mag-btn"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            E-mail me!
          </motion.a>
          <motion.a
            href="https://github.com/Jrix-G"
            target="_blank"
            rel="noreferrer"
            className="btn-line mag-btn"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            GitHub ↗
          </motion.a>
        </motion.div>
      </motion.section>

      {/* Footer */}
      <motion.footer
        className="pf-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <pre className="ascii-sat" aria-hidden="true">{`
      .  *  .    ___    .  *  .
   *   .  .    _[___]_    .    *
     .  .  .  |_o _ o_|  .  . *
   *    . =====<|___|>===== . *
          *  .  /  |  \\  .
         .    *    *     .
`}</pre>
        <p>© Jason Mourier — 03-2026 · Transmitting from Geneva</p>
        <div className="footer-links">
          <motion.a
            href="https://github.com/Jrix-G"
            target="_blank"
            rel="noreferrer"
            whileHover={{ color: 'rgba(92, 225, 230, 1)' }}
          >
            GitHub
          </motion.a>
          <motion.a
            href="mailto:jrixweb@gmail.com"
            whileHover={{ color: 'rgba(92, 225, 230, 1)' }}
          >
            Email
          </motion.a>
        </div>
      </motion.footer>
    </>
  );
};

export default Portfolio;
